import React, { useState, useMemo } from 'react';

import { formatDateTimeAsStored } from "../../utils/utils";

const DealNoteList = ({ dealNotesData, handleLoanSelection, acceptanceDate }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Filter out loans earlier than acceptanceDate when set (hide earlier loans)
  const filteredData = useMemo(() => {
    if (!acceptanceDate) return dealNotesData;
    const cutoff = new Date(acceptanceDate).getTime();
    return dealNotesData.filter((item) => {
      const created = item.metadata?.createdAt;
      if (!created) return true;
      const t = new Date(created).getTime();
      return !isNaN(t) && t >= cutoff;
    });
  }, [dealNotesData, acceptanceDate]);

  // Sort by loanId in ascending order
  const sortedDealNotesData = useMemo(() => {
    return [...filteredData].sort((a, b) => {
      const idA = parseInt(a.loanId);
      const idB = parseInt(b.loanId);
      return idA - idB;
    });
  }, [filteredData]);

  // Calculate total pages
  const totalPages = Math.ceil(sortedDealNotesData.length / itemsPerPage);
  
  // Get current items
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedDealNotesData.slice(indexOfFirstItem, indexOfLastItem);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  return (
    <div className="loan-container">
      <table className="loan-table">
        <thead>
          <tr>
            <th>Loan ID</th>
            <th>Loan Name</th>
            <th>Parent Loan Name</th>
            <th>Transaction Hash</th>
            <th>Created Date</th>
          </tr>
        </thead>
        <tbody>
          {currentItems.map(({ loanId, metadata }, index) => (
            <tr key={index} onClick={() => handleLoanSelection(loanId)}>
              <td>{loanId}</td>
              <td>{metadata?.loanName || 'N/A'}</td>
              <td>{metadata?.loanParent || 'N/A'}</td>
              <td onClick={(e) => e.stopPropagation()}>
                {metadata?.loanTxHash ? (
                  <span
                    className="tx-hash"
                    title={metadata.loanTxHash}
                    onClick={() => window.open(`${process.env.REACT_APP_EXP_URL}${metadata.loanTxHash}`, '_blank')}
                  >
                    {metadata.loanTxHash.slice(0, 8)}…{metadata.loanTxHash.slice(-8)}
                  </span>
                ) : (
                  <span className="muted">—</span>
                )}
              </td>
              <td>{formatDateTimeAsStored(metadata?.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="pagination">
        <button
          className="arrow-button"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          &larr;
        </button>

        {(() => {
          const pages = [];
          let startPage = Math.max(1, currentPage - 2);
          let endPage = Math.min(totalPages, startPage + 4);

          // Adjust start page if we're near the end
          if (endPage - startPage < 4) {
            startPage = Math.max(1, endPage - 4);
          }

          for (let i = startPage; i <= endPage; i++) {
            pages.push(
              <button
                key={i}
                onClick={() => handlePageChange(i)}
                className={`page-button ${currentPage === i ? "active" : ""}`}
              >
                {i}
              </button>
            );
          }

          return pages;
        })()}

        <button
          className="arrow-button"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          &rarr;
        </button>
      </div>
    </div>
  );
};

export default DealNoteList;